import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { EmptyState } from '../../components/ui/PanelCard';
import Modal from '../../components/ui/Modal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import CopyButton from '../../components/ui/CopyButton';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  ItemsBox, StepsList, KillTargets, itemShort, ago, utcStamp, utcDate, SearchInput, SegFilter, TabHead, validLeaf, revealBelow,
  IconCloudOk, IconAlert, IconFolder, IconChevron, IconTicket, IconBook, IconRestore,
  IconDownload, IconTrash, IconPencil, IconPlus, IconShield,
} from './routeUi';

/**
 * ── A SAJÁT ROUTE-OK ("Routes" fül) ─────────────────────────────────────────
 *
 * Minden route, amit a vevő a botban elment: a loader felviszi, és minden gépére
 * leviszi (terv 3., 4.3). Itt kezelhető: verziók és visszaállítás, átnevezés /
 * mappába tétel, törlés (30 napig visszaállítható), letöltés, megosztó kód,
 * beküldés a közösségi könyvtárba (jóváhagyás után kerül ki, terv 8.).
 *
 * A lista ugyanúgy mappák szerint áll, mint a bot "MY ROUTES" listája — egy
 * route a két helyen ugyanúgy néz ki.
 */

// A loader ütközés-másolata: "<név> - cloud ÉÉÉÉHHNN-óópp" (terv 4.3, 4. eset).
const CONFLICT_RE = / - cloud \d{8}-\d{4}$/;

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'shared', label: 'Shared by me' },
  { id: 'library', label: 'In library' },
  { id: 'conflicts', label: 'Conflicts' },
  { id: 'deleted', label: 'Deleted' },
];

function matches(item, filter, q) {
  if (q) {
    const s = q.toLowerCase();
    if (!item.key.toLowerCase().includes(s) && !(item.name || '').toLowerCase().includes(s)) return false;
  }
  if (filter === 'shared') return !!item.share;
  if (filter === 'library') return !!item.library;
  if (filter === 'conflicts') return CONFLICT_RE.test(item.leaf);
  return true;
}

export default function RoutesTab() {
  const { toast } = useToast();
  // A toast objektum minden toast-nál új — ref-en át hívjuk, különben a rá épülő
  // effekt egy hiba-toast után újra lefutna (és újra hibázna).
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [filter, setFilter] = useState('all');
  const [data, setData] = useState(null);
  const [allCounts, setAllCounts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [q, setQ] = useState('');
  const [selId, setSelId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const detailRef = useRef(null);
  const select = useCallback((id) => { setSelId(id); revealBelow(detailRef); }, []);

  // A "Deleted" szűrő MÁS listát kér (a sírokat): a válaszra ráírjuk, melyik
  // nézethez tartozik, és csak a mostanihoz illőt rajzoljuk ki — szűrőváltáskor a
  // régi válasz még itt van. Egy késve érkező régi válasz pedig nem írja felül.
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const load = useCallback(async () => {
    const deletedView = filter === 'deleted';
    try {
      const res = await api.get('/routes', { params: deletedView ? { deleted: 1 } : {} });
      if ((filterRef.current === 'deleted') !== deletedView) return;
      setData({ ...res.data, deletedView });
      setLoadError(null);
      if (!deletedView) {
        const items = res.data.items;
        setAllCounts({
          all: items.length,
          shared: items.filter(i => i.share).length,
          library: items.filter(i => i.library).length,
          conflicts: items.filter(i => CONFLICT_RE.test(i.leaf)).length,
          deleted: res.data.totals.deleted,
        });
      }
    } catch (err) {
      if ((filterRef.current === 'deleted') !== deletedView) return;
      const msg = err.response?.data?.error || 'Could not load your routes.';
      setLoadError({ deletedView, msg });
      toastRef.current.error(msg);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const res = await api.get(`/routes/${id}`);
      setDetail(res.data);
    } catch (err) {
      setDetail(null);
      toastRef.current.error(err.response?.data?.error || 'Could not load the route.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadDetail(selId); }, [selId, loadDetail]);

  const shown = data && data.deletedView === (filter === 'deleted') ? data : null;

  // Ha a kijelölt route eltűnt a listából (törölve, másik szűrő), a kijelölés is megy.
  useEffect(() => {
    if (shown && selId && !shown.items.some(i => i.id === selId)) setSelId(null);
  }, [shown, selId]);

  const refresh = useCallback(async () => {
    await load();
    if (selId) await loadDetail(selId);
  }, [load, loadDetail, selId]);

  const groups = useMemo(() => {
    const items = (shown?.items || []).filter(i => matches(i, filter, q));
    if (filter === 'deleted') return { root: items, folders: [] };
    const folders = new Map();
    const root = [];
    for (const it of items) {
      if (!it.folder) root.push(it);
      else {
        if (!folders.has(it.folder)) folders.set(it.folder, []);
        folders.get(it.folder).push(it);
      }
    }
    const names = [...folders.keys()].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
    return { root, folders: names.map(n => [n, folders.get(n)]) };
  }, [shown, filter, q]);

  const toggleFolder = (name) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  // ── MŰVELETEK ──
  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (okMsg) toastRef.current.success(typeof okMsg === 'function' ? okMsg(res) : okMsg);
      await refresh();
      return res;
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Something went wrong.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const download = async (it) => {
    try {
      const res = await api.get(`/routes/${it.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${it.leaf}.route`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toastRef.current.error('Could not download the route.');
    }
  };

  const totals = data?.totals;
  const counts = allCounts || {};

  return (
    <div className="flex flex-col gap-6">
      <TabHead title="Routes"
               right={(
                 <button type="button" className="btn-glass btn-sm" onClick={() => setModal('redeem')}>
                   <IconTicket className="w-[15px] h-[15px]" /> Redeem a code
                 </button>
               )}>
        Every route you save in the bot lands here and on each PC where you run the loader.
      </TabHead>

      <SummaryStrip totals={totals} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegFilter
          label="Filter routes"
          value={filter}
          onChange={(id) => { setFilter(id); setSelId(null); }}
          items={FILTERS.map(f => ({ ...f, count: counts[f.id], hot: f.id === 'conflicts' && counts[f.id] > 0 }))}
        />
        <SearchInput value={q} onChange={setQ} placeholder="Search routes" className="w-full sm:w-[260px]" />
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_400px] items-start">
        <div className="card overflow-hidden">
          <div className="flex justify-between px-5 py-2.5 border-b border-line-faint table-head">
            <span>{filter === 'deleted' ? 'Deleted route' : 'Route'}</span>
            <span className="pr-8">{filter === 'deleted' ? 'Deleted' : 'Changed'}</span>
          </div>

          {!shown ? (
            loadError && loadError.deletedView === (filter === 'deleted')
              ? <p className="px-5 py-8 text-sm text-[#ff8a80]">{loadError.msg}</p>
              : <div className="py-12"><LoadingSpinner label="Loading your routes…" /></div>
          ) : groups.root.length === 0 && groups.folders.length === 0 ? (
            <EmptyState
              icon={<path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />}
              title={filter === 'all' && !q ? 'No routes in your cloud yet.' : 'Nothing matches.'}
              hint={filter === 'all' && !q
                ? 'Save a route in the bot while the loader runs, and it shows up here within seconds.'
                : undefined}
            />
          ) : (
            <>
              {groups.root.map(it => (
                <RouteRow key={it.id} it={it} selected={it.id === selId} onSelect={select} deleted={filter === 'deleted'} />
              ))}
              {groups.folders.map(([name, items]) => {
                const open = !collapsed.has(name);
                return (
                  <div key={name}>
                    <button
                      type="button"
                      onClick={() => toggleFolder(name)}
                      aria-expanded={open}
                      className="w-full flex items-center gap-2 px-5 py-[9px] bg-bar border-b border-line-faint text-left
                                 font-mono text-[11px] uppercase tracking-[0.1em] text-gray-400 hover:text-gray-50 transition-colors"
                    >
                      <IconChevron open={open} className="w-3.5 h-3.5 text-gray-600" />
                      <IconFolder className="w-3.5 h-3.5 text-gray-600" />
                      {name} <span className="text-gray-600">{items.length}</span>
                    </button>
                    {open && items.map(it => (
                      <RouteRow key={it.id} it={it} indent selected={it.id === selId} onSelect={select} />
                    ))}
                  </div>
                );
              })}
            </>
          )}
          <div className="px-5 py-3 text-[12.5px] text-gray-500">
            {filter === 'deleted'
              ? `Deleted routes stay restorable for ${totals?.keepDays ?? 30} days, then they are gone for good.`
              : 'While the loader runs, a route you save in the bot reaches your account within a few seconds.'}
          </div>
        </div>

        <div ref={detailRef} className="scroll-mt-nav min-w-0">
          <RouteDetail
            detail={selId ? detail : null}
            loading={detailLoading}
            deletedView={filter === 'deleted'}
            busy={busy}
            onRestoreRev={(rev) => run(() => api.post(`/routes/${detail.id}/restore`, { rev }), `Version r${rev} is the newest again.`)}
            onUndelete={() => run(() => api.post(`/routes/${detail.id}/undelete`), 'Route restored. It comes back on your PCs too.')}
            onCreateShare={() => run(() => api.post(`/routes/${detail.id}/share`), 'Share code created.')}
            onRevokeShare={() => run(() => api.delete(`/routes/${detail.id}/share`), 'Share code revoked.')}
            onWithdraw={(what) => run(() => api.post(`/library/${detail.library.id}/withdraw`, { what }),
              what === 'listing' ? 'Taken out of the library.' : 'Submission withdrawn.')}
            onSubmit={() => setModal('submit')}
            onRename={() => setModal('rename')}
            onDelete={() => setModal('delete')}
            onDownload={() => download(detail)}
          />
        </div>
      </div>

      {modal === 'redeem' && (
        <RedeemModal
          onClose={() => setModal(null)}
          onDone={async (res) => { setModal(null); await load(); if (res?.id) select(res.id); }}
        />
      )}
      {modal === 'submit' && detail && (
        <SubmitModal
          detail={detail}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); await refresh(); }}
        />
      )}
      {modal === 'rename' && detail && (
        <RenameModal
          detail={detail}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); await refresh(); }}
        />
      )}
      {modal === 'delete' && detail && (
        <ConfirmModal
          title="Delete this route?"
          message={`"${detail.key}" disappears from your account and from every PC that syncs it. You can restore it here for 30 days.`}
          confirmLabel="Delete"
          danger
          onCancel={() => setModal(null)}
          onConfirm={async () => {
            setModal(null);
            await run(() => api.delete(`/routes/${detail.id}`), 'Route deleted. Restore it from the Deleted tab within 30 days.');
          }}
        />
      )}
    </div>
  );
}

// ── ÖSSZEGZŐ SÁV ──────────────────────────────────────────────────────────────
function SummaryStrip({ totals }) {
  const cell = 'px-5 py-4 flex flex-col gap-1 min-w-0 border-line-faint';
  const big = 'text-[22px] font-semibold leading-[1.2] text-gray-50';
  const sub = 'text-[12.5px] text-gray-500 truncate';
  return (
    <div className="card grid grid-cols-2 lg:grid-cols-4">
      <div className={cell}>
        <span className="panel-label">Last change</span>
        <b className={big}>{totals?.lastChangeAt ? ago(totals.lastChangeAt) : '—'}</b>
        <span className={sub}>
          {totals?.lastChangeBy ? (totals.lastChangeBy === 'web' ? 'from this website' : `from ${totals.lastChangeBy}`) : 'nothing synced yet'}
        </span>
      </div>
      <div className={`${cell} border-l`}>
        <span className="panel-label">Routes</span>
        <b className={big}>
          {totals?.live ?? '—'} <small className="text-[13px] font-normal text-gray-500">/ {totals?.max ?? 200}</small>
        </b>
        <span className={sub}>{totals?.folders != null ? `${totals.folders} ${totals.folders === 1 ? 'folder' : 'folders'}` : ' '}</span>
      </div>
      <div className={`${cell} border-t lg:border-t-0 lg:border-l`}>
        <span className="panel-label">PCs</span>
        <b className={big}>{totals?.machines?.length ?? 0}</b>
        <span className={sub} title={(totals?.machines || []).join(' · ')}>
          {(totals?.machines || []).join(' · ') || 'no PC has synced yet'}
        </span>
      </div>
      <div className={`${cell} border-t border-l lg:border-t-0`}>
        <span className="panel-label">Deleted</span>
        <b className={big}>{totals?.deleted ?? 0}</b>
        <span className={sub}>Restorable for {totals?.keepDays ?? 30} days</span>
      </div>
    </div>
  );
}

// ── EGY SOR A LISTÁBAN ────────────────────────────────────────────────────────
function RouteRow({ it, selected, onSelect, indent, deleted }) {
  const conflict = CONFLICT_RE.test(it.leaf);
  const items = itemShort(it.summary?.items);
  return (
    <button
      type="button"
      onClick={() => onSelect(it.id)}
      aria-pressed={selected}
      className={`w-full text-left flex items-center gap-3.5 py-[11px] pr-5 border-b border-line-faint transition-colors duration-150 ${
        indent ? 'pl-10' : 'pl-5'
      } ${selected ? 'bg-gray-850 shadow-[inset_2px_0_0_#a48bff]' : conflict ? 'bg-warn/[0.04] hover:bg-white/[0.04]' : 'hover:bg-white/[0.04]'}`}
    >
      <div className="min-w-0 flex-1">
        <b className="block text-sm font-medium text-gray-50 truncate">{deleted ? it.key : it.leaf}</b>
        <div className="mt-[3px] flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[12.5px] text-gray-500">
          <span>{it.summary?.steps ?? 0} {it.summary?.steps === 1 ? 'step' : 'steps'}</span>
          {items && <span className="text-warn">uses {items}</span>}
          {conflict && <span className="badge-warn">Conflict copy</span>}
          {it.share && <span className="badge-brand">Shared</span>}
          {it.library?.status === 'live' && <span className="badge-brand">In library</span>}
          {(it.library?.status === 'pending' || it.library?.pending) && <span className="badge-inactive">In review</span>}
          {it.library?.status === 'hidden' && <span className="badge-banned">Hidden by staff</span>}
          {it.library?.status === 'rejected' && <span className="badge-banned">Rejected</span>}
          {it.source && (
            <span>from {it.source.user}{it.source.kind === 'share' ? ', by code' : it.source.kind === 'library' ? ', route library' : ''}</span>
          )}
        </div>
      </div>
      <div className="w-[110px] flex-shrink-0 text-right flex flex-col">
        <span className="text-[13px] text-gray-400">{ago(deleted ? it.deletedAt : it.changedAt)}</span>
        <small className="font-mono text-[11px] text-gray-600 truncate">{it.changedBy === 'web' ? 'website' : (it.changedBy || '')}</small>
      </div>
      {deleted ? (
        <IconTrash className="w-[18px] h-[18px] text-gray-500 flex-shrink-0" />
      ) : conflict ? (
        <IconAlert className="w-[18px] h-[18px] text-warn flex-shrink-0" aria-label="Conflict copy" />
      ) : (
        <IconCloudOk className="w-[18px] h-[18px] text-ok flex-shrink-0" aria-label="In the cloud" />
      )}
    </button>
  );
}

// ── A KIJELÖLT ROUTE ──────────────────────────────────────────────────────────
function RouteDetail({
  detail, loading, deletedView, busy,
  onRestoreRev, onUndelete, onCreateShare, onRevokeShare, onWithdraw, onSubmit, onRename, onDelete, onDownload,
}) {
  const [allRevs, setAllRevs] = useState(false);
  useEffect(() => { setAllRevs(false); }, [detail?.id]);

  if (loading && !detail) {
    return <aside className="card p-8 flex justify-center"><LoadingSpinner label="Loading…" /></aside>;
  }
  if (!detail) {
    return (
      <aside className="card px-6 py-10 text-center text-sm text-gray-500">
        Pick a route to see its steps, versions and sharing.
      </aside>
    );
  }

  const s = detail.summary || {};
  const deleted = !!detail.deletedAt;
  const revs = allRevs ? detail.revisions : detail.revisions.slice(0, 4);
  const conflict = CONFLICT_RE.test(detail.leaf);
  const lib = detail.library;

  return (
    <aside className="card overflow-hidden" aria-label="Selected route">
      <div className="min-h-[52px] px-5 py-3 flex items-center justify-between gap-3 border-b border-line-faint">
        <span className="panel-label">{deleted ? 'Deleted route' : 'Route'}</span>
        <div className="flex gap-1.5 flex-wrap justify-end">
          {!deleted && <span className="badge-active">In sync</span>}
          {detail.share && <span className="badge-brand">Shared</span>}
          {conflict && <span className="badge-warn">Conflict copy</span>}
        </div>
      </div>

      <div className="px-5 py-4 flex flex-col gap-2">
        <b className="text-[17px] font-semibold text-gray-50 leading-snug break-words">{detail.name || detail.leaf}</b>
        <div className="font-mono text-xs text-gray-600 break-all">
          {detail.leaf}.route · {detail.folder ? `folder ${detail.folder}` : 'no folder'}
        </div>
        {detail.source && (
          <div className="text-[12.5px] text-gray-500">
            A copy from <span className="text-gray-100">{detail.source.user}</span>
            {detail.source.kind === 'share' ? ' (share code)' : detail.source.kind === 'library' ? ' (route library)' : ''}
            {' '}— their later edits do not change it.
          </div>
        )}
        {conflict && (
          <div className="mt-2 text-[12.5px] text-warn">
            This route was changed on two PCs at once. Keep one, delete the other.
          </div>
        )}
        <div className="grid grid-cols-3 gap-2 mt-2">
          <Fact label="Steps" value={s.steps ?? 0} />
          <Fact label="Rail points" value={s.points ?? 0} />
          <Fact label="Version" value={`r${detail.rev}`} />
        </div>
      </div>

      {deleted ? (
        <Section label="Deleted">
          <span className="text-[13px] text-gray-400">Deleted {utcStamp(detail.deletedAt)} UTC.</span>
          <button type="button" className="btn-primary btn-sm self-start" disabled={busy} onClick={onUndelete}>
            <IconRestore className="w-[15px] h-[15px]" /> Restore this route
          </button>
        </Section>
      ) : null}

      <Section label="Steps">
        <StepsList maps={s.maps} entry={s.entry} names={s.names} />
        <KillTargets kills={s.kills} names={s.names} />
      </Section>

      <Section label="Uses items">
        <ItemsBox items={s.items} title={null} names={s.names} />
      </Section>

      <Section label="Versions" right={<span className="text-xs text-gray-600">the last 20 are kept</span>}>
        {revs.map(v => (
          <div key={v.rev} className="flex items-center gap-2.5 text-[13px]">
            <code className="w-[30px] font-mono text-xs text-gray-50">r{v.rev}</code>
            <span className="flex-1 min-w-0 text-gray-400 truncate">
              {utcStamp(v.at)} · {v.deleted ? 'deleted' : v.origin === 'move' ? `moved to ${v.key}` : v.origin === 'restore' ? 'restored' : (v.changedBy === 'web' || !v.changedBy ? 'website' : v.changedBy)}
            </span>
            {v.rev === detail.rev ? (
              <span className="badge-inactive">Current</span>
            ) : v.restorable && !deleted ? (
              <button type="button" className="btn-ghost btn-sm h-[26px] px-2" disabled={busy} onClick={() => onRestoreRev(v.rev)}>
                <IconRestore className="w-3.5 h-3.5" /> Restore
              </button>
            ) : null}
          </div>
        ))}
        {detail.revisions.length > 4 && (
          <button type="button" className="link text-[13px] self-start" onClick={() => setAllRevs(v => !v)}>
            {allRevs ? 'Show fewer' : `Show all ${detail.revisions.length} versions`}
          </button>
        )}
      </Section>

      {!deleted && (
        <Section label="Share code">
          {detail.share ? (
            <>
              <div className="flex items-center gap-2">
                <span className="flex-1 min-w-0 px-3 py-[7px] rounded-lg bg-bar border border-line font-mono text-[17px] font-medium tracking-[0.12em] text-gray-50">
                  {detail.share.code}
                </span>
                <CopyButton value={detail.share.code} variant="button" className="btn-sm" />
              </div>
              <span className="text-xs text-gray-500">
                Redeemed {detail.share.uses} {detail.share.uses === 1 ? 'time' : 'times'}. Whoever redeems it gets their own copy. Your later edits do not reach them.
              </span>
              <button type="button" className="btn-ghost btn-sm px-0 text-[#ff8a80] hover:bg-transparent self-start" disabled={busy} onClick={onRevokeShare}>
                Revoke code
              </button>
            </>
          ) : detail.sharingBlocked ? (
            <span className="text-[13px] text-gray-500">Sharing is turned off for your account.</span>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] text-gray-500">No share code yet.</span>
              <button type="button" className="btn-glass btn-sm" disabled={busy} onClick={onCreateShare}>
                <IconPlus className="w-3.5 h-3.5" /> Create share code
              </button>
            </div>
          )}
        </Section>
      )}

      {!deleted && (
        <Section label="Route library">
          <LibraryState lib={lib} busy={busy} blocked={detail.sharingBlocked}
                        onSubmit={onSubmit} onWithdraw={onWithdraw} />
        </Section>
      )}

      {!deleted && (
        <div className="flex flex-wrap gap-2 px-5 py-3 border-t border-line-faint bg-bar">
          <button type="button" className="btn-glass btn-sm" onClick={onDownload}><IconDownload className="w-3.5 h-3.5" /> Download .route</button>
          <button type="button" className="btn-glass btn-sm" disabled={busy} onClick={onRename}><IconPencil className="w-3.5 h-3.5" /> Rename or move</button>
          <button type="button" className="btn-danger btn-sm ml-auto" disabled={busy} onClick={onDelete}><IconTrash className="w-3.5 h-3.5" /> Delete</button>
        </div>
      )}
    </aside>
  );
}

function LibraryState({ lib, busy, blocked, onSubmit, onWithdraw }) {
  if (!lib) {
    return (
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] text-gray-500">Not in the library.</span>
        <button type="button" className="btn-glass btn-sm" disabled={busy || blocked} onClick={onSubmit}
                title={blocked ? 'Publishing is turned off for your account.' : undefined}>
          <IconBook className="w-3.5 h-3.5" /> Submit to library
        </button>
      </div>
    );
  }
  const pending = lib.pending;
  return (
    <div className="flex flex-col gap-2">
      {lib.status === 'live' && (
        <span className="text-[13px] text-gray-400">
          In the library as <span className="text-gray-50">“{lib.liveTitle}”</span> · added {lib.adds} {lib.adds === 1 ? 'time' : 'times'}
        </span>
      )}
      {lib.status === 'pending' && (
        <span className="text-[13px] text-gray-400">
          <span className="badge-warn mr-2">In review</span>
          A tester or an admin checks it before it shows up.
        </span>
      )}
      {lib.status === 'hidden' && (
        <span className="text-[13px] text-gray-400">
          <span className="badge-banned mr-2">Hidden by staff</span>
          {lib.hiddenReason ? `Reason: ${lib.hiddenReason}` : 'It is not shown in the library right now.'}
        </span>
      )}
      {lib.status === 'rejected' && (
        <span className="text-[13px] text-gray-400">
          <span className="badge-banned mr-2">Rejected</span>
          {lib.rejected?.reason ? `Reason: ${lib.rejected.reason}` : 'You can fix it and submit again.'}
        </span>
      )}
      {pending && lib.status !== 'pending' && (
        <span className="text-[12.5px] text-gray-500">
          Update v{pending.version_no} is in review since {utcDate(pending.submitted_at)}. The current one stays until then.
        </span>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-glass btn-sm" disabled={busy || blocked} onClick={onSubmit}>
          <IconBook className="w-3.5 h-3.5" />
          {lib.status === 'rejected' ? 'Submit again' : pending ? 'Replace submission' : 'Submit an update'}
        </button>
        {pending && (
          <button type="button" className="btn-ghost btn-sm" disabled={busy} onClick={() => onWithdraw('pending')}>
            Withdraw submission
          </button>
        )}
        {(lib.status === 'live' || lib.status === 'hidden') && (
          <button type="button" className="btn-ghost btn-sm text-[#ff8a80]" disabled={busy} onClick={() => onWithdraw('listing')}>
            Take out of library
          </button>
        )}
      </div>
    </div>
  );
}

function Section({ label, right, children }) {
  return (
    <div className="flex flex-col gap-2.5 px-5 py-3.5 border-t border-line-faint">
      <div className="flex items-center justify-between gap-3"><span className="panel-label">{label}</span>{right}</div>
      {children}
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="px-2.5 py-2 rounded-lg bg-bar border border-line">
      <span className="block font-mono text-[10.5px] uppercase tracking-[0.1em] text-gray-500">{label}</span>
      <b className="text-[17px] font-semibold text-gray-50">{value}</b>
    </div>
  );
}

// ── KÓD BEVÁLTÁSA ─────────────────────────────────────────────────────────────
function RedeemModal({ onClose, onDone }) {
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const lookUp = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(''); setPreview(null);
    try {
      const res = await api.get(`/routes/redeem/${encodeURIComponent(code.trim())}`);
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not look up this code.');
    } finally { setBusy(false); }
  };

  const add = async () => {
    setBusy(true);
    try {
      const res = await api.post('/routes/redeem', { code: code.trim() });
      toast.success(res.data.already ? 'It is already in your routes.' : `Added as ${res.data.key}.`);
      onDone(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not redeem this code.');
    } finally { setBusy(false); }
  };

  return (
    <Modal
      title="Redeem a share code"
      icon={<IconTicket className="w-5 h-5" />}
      onClose={onClose}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={!preview || preview.already || busy} onClick={add}>
            <IconPlus className="w-4 h-4" /> Add to my routes
          </button>
        </div>
      }
    >
      <form onSubmit={lookUp} className="flex flex-col gap-2">
        <label htmlFor="rd-code" className="label">Share code</label>
        <div className="flex gap-2">
          <input id="rd-code" className="input font-mono uppercase tracking-[0.14em]" placeholder="ME-K7QF-2MZD"
                 value={code} onChange={e => { setCode(e.target.value); setPreview(null); setError(''); }} autoFocus />
          <button type="submit" className="btn-glass" disabled={busy || code.trim().length < 8}>Look up</button>
        </div>
      </form>

      {error && <p className="mt-3 text-sm text-[#ff8a80]">{error}</p>}

      {preview && (
        <div className="mt-4 flex flex-col gap-4">
          <div className="surface-sunken p-4 flex flex-col gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-[15px] font-semibold text-gray-50 break-words">{preview.name}</span>
                <span className="text-[12.5px] text-gray-500">Shared by {preview.owner}</span>
              </div>
              <span className="badge-active">Valid code</span>
            </div>
            <StepsList maps={preview.summary?.maps} entry={preview.summary?.entry} names={preview.summary?.names} />
            <ItemsBox items={preview.summary?.items} title="When it runs, this route uses:" names={preview.summary?.names} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="label mb-0">{preview.already ? 'Already in your routes as' : 'Saved as'}</span>
            <span className="font-mono text-[13px] text-gray-100 break-all">{preview.savedAs}.route</span>
            {!preview.already && (
              <span className="text-[12.5px] text-gray-500">
                Your own copy: it does not change when {preview.owner} edits theirs, and it reaches every PC where you run the loader.
              </span>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── BEKÜLDÉS A KÖNYVTÁRBA ─────────────────────────────────────────────────────
function SubmitModal({ detail, onClose, onDone }) {
  const { toast } = useToast();
  const lib = detail.library;
  // A legutóbbi beküldés szövege jön vissza (várakozó, elutasított vagy a kint lévő),
  // nem kell elölről írni.
  const latest = lib?.latest;
  const [title, setTitle] = useState(latest?.title || lib?.pending?.title || lib?.liveTitle || detail.name || detail.leaf);
  const [description, setDescription] = useState(latest?.description || '');
  // „What changed”: csak egy MÁR KINT LÉVŐ tétel frissítésénél — a játékosok a tétel
  // oldalán látják. A még várakozó / elutasított frissítés sora visszajön; a kint
  // lévőé nem, mert az új frissítéshez új sor kell.
  const isUpdate = !!lib?.liveVersion;
  const [changes, setChanges] = useState(
    latest && latest.status !== 'approved' && latest.status !== 'superseded' ? (latest.changes || '') : ''
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const items = detail.summary?.items || [];

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const res = await api.post('/library/submit', {
        routeId: detail.id, title, description, changes: isUpdate ? changes : '',
      });
      toast.success(res.data.unchanged
        ? 'Nothing changed since the approved version — nothing to review.'
        : 'Submitted. A tester or an admin checks it before it shows up in the library.');
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not submit.');
    } finally { setBusy(false); }
  };

  return (
    <Modal
      title={lib ? 'Submit an update to the library' : 'Submit to the route library'}
      subtitle={`${detail.key} · r${detail.rev}`}
      icon={<IconBook className="w-5 h-5" />}
      onClose={onClose}
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={busy || title.trim().length < 3} onClick={submit}>
            <IconBook className="w-4 h-4" /> Submit for review
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="px-4 py-3.5 rounded-panel bg-gray-900 border border-line flex gap-3 items-start text-sm text-gray-100">
          <IconShield className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
          <span>
            A tester or an admin checks it first. Until it is approved it is not in the library; you see the result here.
            {lib?.status === 'live' && ' The version that is in the library now stays there until the update is approved.'}
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="sb-title" className="label mb-0">Title</label>
          <input id="sb-title" className="input" maxLength={80} value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="sb-desc" className="label mb-0">Description</label>
          <textarea id="sb-desc" className="textarea" maxLength={1000} rows={3} value={description}
                    placeholder="What does it farm, what does it need, anything to know before running it?"
                    onChange={e => setDescription(e.target.value)} />
        </div>
        {isUpdate && (
          <div className="flex flex-col gap-2">
            <label htmlFor="sb-changes" className="label mb-0">What changed</label>
            <textarea id="sb-changes" className="textarea" maxLength={300} rows={2} value={changes}
                      placeholder="e.g. Path updated, mobs added to the kill list"
                      onChange={e => setChanges(e.target.value)} />
            <span className="text-[12.5px] text-gray-500">
              Players see it on the route's library page once the update is approved.
            </span>
          </div>
        )}
        <div className="surface-sunken p-4 flex flex-col gap-2 text-[13px] text-gray-400">
          <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-gray-500">What everyone will see once it is approved</span>
          <span>· Your username as the author</span>
          <span>· The steps, the kill targets and the items this route uses</span>
          <span>· A copy of version r{detail.rev}. Your later edits stay private until you submit an update.</span>
        </div>
        {items.length > 0 && (
          <ItemsBox items={items} names={detail.summary?.names}
                    title="This route uses items. The listing shows it, and players confirm it before adding." />
        )}
        {error && <p className="text-sm text-[#ff8a80]">{error}</p>}
      </div>
    </Modal>
  );
}

// ── ÁTNEVEZÉS / MAPPÁBA TÉTEL ─────────────────────────────────────────────────
function RenameModal({ detail, onClose, onDone }) {
  const { toast } = useToast();
  const [folder, setFolder] = useState(detail.folder || '');
  const [name, setName] = useState(detail.leaf);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const okName = validLeaf(name.trim());
  const okFolder = folder.trim() === '' || validLeaf(folder.trim());

  const save = async () => {
    const key = folder.trim() ? `${folder.trim()}\\${name.trim()}` : name.trim();
    setBusy(true); setError('');
    try {
      await api.patch(`/routes/${detail.id}`, { key });
      toast.success(`Now "${key}". Your PCs pick it up on their next sync.`);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not rename.');
    } finally { setBusy(false); }
  };

  return (
    <Modal
      title="Rename or move"
      subtitle={detail.key}
      icon={<IconPencil className="w-5 h-5" />}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={busy || !okName || !okFolder} onClick={save}>Save</button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="rn-folder" className="label mb-0">Folder (empty = no folder)</label>
          <input id="rn-folder" className="input" maxLength={63} value={folder} onChange={e => setFolder(e.target.value)} />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="rn-name" className="label mb-0">Name</label>
          <input id="rn-name" className="input" maxLength={120} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <p className="text-[12.5px] text-gray-500">
          Letters, digits, spaces and - _ . only. The bot uses this as the file name, so brackets are not allowed.
        </p>
        {(!okName || !okFolder) && <p className="text-[12.5px] text-warn">That name cannot be a file name.</p>}
        {error && <p className="text-sm text-[#ff8a80]">{error}</p>}
      </div>
    </Modal>
  );
}
